import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import type { SelectOption } from '@rio-cloud/rio-uikit/Select';
import { fetchBudgetUserProfile } from '../../../../api/fetchBudget';
import { fetchDealershipsByDN } from '../../../../api/fetchDealerships';
import { fetchAllDnsBySearch, fetchDnsOptions } from '../../../../api/fetchNewSupportCall';
import { fetchSupportRegionals } from '../../../../api/tas';

type AegDnOption = { id: string; label: string };

const useAegDealerships = () => {
    const intl = useIntl();
    const [role, setRole] = useState('');
    const [dnOptions, setDnOptions] = useState<SelectOption[]>([]);
    const [regionalOptions, setRegionalOptions] = useState<SelectOption[]>([]);
    const regionalRequestId = useRef(0);

    useEffect(() => {
        const loadRole = async () => {
            setRole((await fetchBudgetUserProfile()) || '');
        };

        const loadDnOptions = async () => {
            const response = await fetchDnsOptions();

            setDnOptions(
                (response?.dns ?? []).map(item => ({
                    id: item.dn,
                    label: intl.formatMessage(
                        { id: 'intl-msg:shared.newSupportCall.dnOptionLabel' },
                        {
                            dn: item.dn,
                            name: item.dnName,
                            address: item.address,
                            neighborhood: item.neighborhood,
                            city: item.city,
                            state: item.state,
                        }
                    ),
                }))
            );
        };

        const loadRegionalOptions = async () => {
            const response = await fetchSupportRegionals();

            setRegionalOptions(
                (response?.content ?? []).map((item: { id: number; name: string }) => ({
                    id: String(item.id),
                    label: item.name,
                }))
            );
        };

        loadRole();
        loadDnOptions();
        loadRegionalOptions();
    }, []);

    const searchDns = async (searchTerm: string, pageSize: number, page: number): Promise<AegDnOption[]> => {
        if (!searchTerm) {
            return [];
        }

        const response = await fetchAllDnsBySearch(searchTerm);
        const options = (response ?? []).map(option => ({
            id: option.id,
            label: intl.formatMessage(
                { id: 'intl-msg:shared.newSupportCall.dnSearchOptionLabel' },
                {
                    dn: option.id,
                    name: option.fantasyName,
                    neighborhood: option.neighborhood,
                    city: option.city,
                    state: option.state,
                }
            ),
        }));

        return options.slice((page - 1) * pageSize, page * pageSize);
    };

    const getRegionalIdByDn = async (dn: string): Promise<string> => {
        const requestId = ++regionalRequestId.current;
        const dealership = await fetchDealershipsByDN(dn);

        if (requestId !== regionalRequestId.current || !dealership?.regional) {
            return '';
        }

        const normalizedRegional = dealership.regional.trim().toLocaleLowerCase();
        const regional = regionalOptions.find(option => String(option.label).replace(/\D/g, '') === normalizedRegional);

        return regional?.id ?? '';
    };

    return { role, dnOptions, regionalOptions, searchDns, getRegionalIdByDn };
};

export default useAegDealerships;
